import React from 'react';
import { Box, LinearProgress, Paper, Stack, Typography } from '@mui/material';
import type { FoodTestId, FoodTestOutcome } from './types';

export interface FoodObservation {
  id: string;
  foodId: string;
  foodName: string;
  test: FoodTestId;
  category: string;
  observation: string;
  inference: string;
  colourName: string;
  dropsAdded?: number;
  dropsRequired?: number;
  timestamp: string;
}

const testLabels: Record<FoodTestId, string> = {
  'reducing-sugar': 'Reducing sugar',
  'non-reducing-sugar': 'Non-reducing sugar',
  starch: 'Starch',
  protein: 'Protein',
  lipid: 'Lipid',
  'vitamin-c': 'Vitamin C',
};

interface FoodTestVisualsProps {
  observations: FoodObservation[];
  currentOutcome: FoodTestOutcome;
}

const FoodTestVisuals: React.FC<FoodTestVisualsProps> = ({ observations, currentOutcome }) => {
  const foods = Array.from(new Set(observations.map((item) => item.foodName)));
  const drops = observations.filter((item) => item.test === 'vitamin-c' && item.dropsRequired);

  return (
    <Stack spacing={2}>
      <Paper variant="outlined" sx={{ p: 2, borderRadius: 1 }}>
        <Typography variant="subtitle1" fontWeight={800} sx={{ mb: 1 }}>Benedict’s colour ladder</Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 0.5 }}>
          {[
            ['Negative', '#1677c8'], ['Trace', '#50a878'], ['Low', '#e6d347'], ['Moderate', '#e99132'], ['High', '#a83b26'],
          ].map(([label, color]) => (
            <Box key={label} sx={{ textAlign: 'center' }}>
              <Box sx={{ height: 22, borderRadius: 0.5, backgroundColor: color, border: currentOutcome.colourName === (label === 'High' ? 'brick red' : label.toLowerCase()) ? '2px solid #183e36' : '1px solid rgba(0,0,0,.18)' }} />
              <Typography variant="caption">{label}</Typography>
            </Box>
          ))}
        </Box>
        <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 1 }}>Current reading: {currentOutcome.colourName} ({currentOutcome.category}).</Typography>
      </Paper>

      <Paper variant="outlined" sx={{ p: 2, borderRadius: 1 }}>
        <Typography variant="subtitle1" fontWeight={800}>Food test results matrix</Typography>
        <Box sx={{ overflowX: 'auto', mt: 1 }}>
          <Box sx={{ minWidth: 650, display: 'grid', gridTemplateColumns: '120px repeat(6, 1fr)', borderTop: '1px solid #dce6e2' }}>
            <Typography variant="caption" fontWeight={800} sx={{ p: 0.75 }}>Food sample</Typography>
            {(Object.keys(testLabels) as FoodTestId[]).map((test) => <Typography key={test} variant="caption" fontWeight={800} sx={{ p: 0.75 }}>{testLabels[test]}</Typography>)}
            {foods.map((foodName) => <React.Fragment key={foodName}>
              <Typography variant="caption" sx={{ p: 0.75, borderTop: '1px solid #edf1ef' }}>{foodName}</Typography>
              {(Object.keys(testLabels) as FoodTestId[]).map((test) => {
                const result = observations.find((item) => item.foodName === foodName && item.test === test);
                return <Typography key={test} variant="caption" aria-label={result ? `${foodName} ${testLabels[test]} ${result.category}` : `${foodName} ${testLabels[test]} not tested`} sx={{ p: 0.75, borderTop: '1px solid #edf1ef', color: result?.category === 'failed' ? 'error.main' : 'text.secondary' }}>
                  {result ? `${result.category}: ${result.colourName}` : '—'}
                </Typography>;
              })}
            </React.Fragment>)}
          </Box>
        </Box>
        {!observations.length && <Typography variant="caption" color="text.secondary">Recorded observations appear here by sample and test.</Typography>}
      </Paper>

      <Paper variant="outlined" sx={{ p: 2, borderRadius: 1 }}>
        <Typography variant="subtitle1" fontWeight={800}>DCPIP comparison</Typography>
        {!drops.length && <Typography variant="caption" color="text.secondary">Record vitamin C tests to compare drops needed for decolourisation.</Typography>}
        <Stack spacing={1} sx={{ mt: 1 }}>
          {drops.map((item) => <Box key={item.id}>
            <Stack direction="row" justifyContent="space-between"><Typography variant="caption">{item.foodName}</Typography><Typography variant="caption">{item.dropsAdded} drops</Typography></Stack>
            <LinearProgress variant="determinate" value={Math.min(100, ((item.dropsRequired || 1) / 100) * 100)} aria-label={`${item.foodName}: ${item.dropsAdded} drops to decolourise`} />
          </Box>)}
        </Stack>
      </Paper>
    </Stack>
  );
};

export default FoodTestVisuals;